import { saveOrShare } from '../../../platform';
import {
  PILLARS,
  VISIBILITY_CAPABILITIES,
  ROADMAP_PHASES,
} from '../config/ztaArchitectureData';

// PDF Export - generates a professional assessment report
export async function generateZtaPdf({ selectedDepartment, capabilityData }) {
  // jspdf is ~325KB minified — loaded on demand at first export, not in main
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Helper to add text with word wrap
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 5) => {
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach((line, i) => {
      if (y + (i * lineHeight) > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, x, y + (i * lineHeight));
    });
    return y + (lines.length * lineHeight);
  };

  // Title Page
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ZERO TRUST', pageWidth / 2, 50, { align: 'center' });
  pdf.text('MATURITY ASSESSMENT', pageWidth / 2, 62, { align: 'center' });

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text('CISA Pillar-Aligned Assessment Report', pageWidth / 2, 80, { align: 'center' });

  pdf.setFontSize(12);
  pdf.text(`Organization: ${selectedDepartment.common_name || selectedDepartment.acronym}`, pageWidth / 2, 100, { align: 'center' });
  pdf.text(`Assessment Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, 110, { align: 'center' });

  // Framework reference
  pdf.setFontSize(10);
  pdf.setTextColor(100);
  pdf.text('Based on CISA Zero Trust Maturity Model v2.0', pageWidth / 2, 130, { align: 'center' });
  pdf.text('AB 869 Compliance Ready', pageWidth / 2, 138, { align: 'center' });
  pdf.setTextColor(0);

  // Page 2 - Executive Summary
  pdf.addPage();
  yPos = margin;

  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Executive Summary', margin, yPos);
  yPos += 12;

  // Calculate overall maturity
  const maturityLevels = { 'TRAD.': 0, 'INITIAL': 1, 'ADVANCED': 2, 'OPTIMAL': 3 };
  const maturityNames = ['Traditional', 'Initial', 'Advanced', 'Optimal'];
  let totalScore = 0;
  let capCount = 0;

  PILLARS.forEach(pillar => {
    pillar.capabilities.forEach(cap => {
      const state = capabilityData[cap.id];
      if (state?.maturity) {
        totalScore += maturityLevels[state.maturity] || 0;
        capCount++;
      }
    });
  });

  const avgMaturity = capCount > 0 ? Math.round(totalScore / capCount) : 0;

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  yPos = addWrappedText(
    `This assessment evaluates ${selectedDepartment.common_name || selectedDepartment.acronym}'s Zero Trust security posture across the five CISA pillars: Identity, Device, Network, Application, and Data, plus cross-cutting Visibility & Analytics capabilities.`,
    margin, yPos, pageWidth - (margin * 2)
  );
  yPos += 8;

  pdf.setFont('helvetica', 'bold');
  pdf.text(`Overall Maturity Level: ${maturityNames[avgMaturity]}`, margin, yPos);
  yPos += 10;

  // Pillar summary
  pdf.setFont('helvetica', 'bold');
  pdf.text('Pillar Maturity Summary:', margin, yPos);
  yPos += 8;

  pdf.setFont('helvetica', 'normal');
  PILLARS.forEach(pillar => {
    let pillarScore = 0;
    let pillarCount = 0;
    pillar.capabilities.forEach(cap => {
      const state = capabilityData[cap.id];
      if (state?.maturity) {
        pillarScore += maturityLevels[state.maturity] || 0;
        pillarCount++;
      }
    });
    const pillarAvg = pillarCount > 0 ? Math.round(pillarScore / pillarCount) : 0;
    pdf.text(`• ${pillar.name}: ${maturityNames[pillarAvg]}`, margin + 5, yPos);
    yPos += 6;
  });

  // Page 3+ - Detailed Pillar Assessments
  PILLARS.forEach(pillar => {
    pdf.addPage();
    yPos = margin;

    // Pillar header
    pdf.setFillColor(pillar.color);
    pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
    pdf.setTextColor(255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(pillar.name + ' PILLAR', margin + 5, yPos + 7);
    pdf.setTextColor(0);
    yPos += 18;

    // Capabilities
    pillar.capabilities.forEach(cap => {
      const state = capabilityData[cap.id] || {};
      const maturity = state.maturity || 'TRAD.';
      const vendors = state.vendors?.length ? state.vendors.join(', ') : 'Not configured';

      if (yPos > pageHeight - 40) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text(cap.name, margin, yPos);
      if (cap.critical) {
        pdf.setTextColor(239, 68, 68);
        pdf.setFontSize(8);
        pdf.text(' (AB 869 Critical)', margin + pdf.getTextWidth(cap.name) + 2, yPos);
        pdf.setTextColor(0);
      }
      yPos += 6;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Maturity: ${maturity}`, margin + 5, yPos);
      yPos += 5;
      pdf.text(`Vendors: ${vendors}`, margin + 5, yPos);
      yPos += 5;

      if (state.notes) {
        yPos = addWrappedText(`Notes: ${state.notes}`, margin + 5, yPos, pageWidth - (margin * 2) - 10, 4);
      }
      yPos += 6;
    });
  });

  // Visibility & Analytics section
  pdf.addPage();
  yPos = margin;

  pdf.setFillColor(139, 92, 246);
  pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
  pdf.setTextColor(255);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('VISIBILITY & ANALYTICS', margin + 5, yPos + 7);
  pdf.setTextColor(0);
  yPos += 18;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  yPos = addWrappedText(
    'Cross-pillar monitoring, detection, and response capabilities that span all five CISA pillars.',
    margin, yPos, pageWidth - (margin * 2)
  );
  yPos += 8;

  VISIBILITY_CAPABILITIES.forEach(cap => {
    const state = capabilityData[cap.id] || {};
    const maturity = state.maturity || 'TRAD.';
    const vendors = state.vendors?.length ? state.vendors.join(', ') : cap.vendors;

    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = margin;
    }

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(cap.name, margin, yPos);
    yPos += 6;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Maturity: ${maturity} | Vendors: ${vendors}`, margin + 5, yPos);
    yPos += 8;
  });

  // Roadmap page
  pdf.addPage();
  yPos = margin;

  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Recommended Roadmap', margin, yPos);
  yPos += 12;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  ROADMAP_PHASES.forEach(phase => {
    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = margin;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.text(`Phase ${phase.phase} (${phase.period})`, margin, yPos);
    yPos += 6;
    pdf.setFont('helvetica', 'normal');
    yPos = addWrappedText(phase.items, margin + 5, yPos, pageWidth - (margin * 2) - 10, 4);
    yPos += 8;
  });

  // Footer on last page
  pdf.setFontSize(8);
  pdf.setTextColor(100);
  pdf.text(
    `Generated by TekStak Zero Trust Assessment Tool | ${new Date().toISOString()}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );

  // Save the PDF — via saveOrShare so the native app gets the share sheet
  // (jsPDF's .save() anchor download silently no-ops in WKWebView)
  const fileName = `ZeroTrust_Assessment_${selectedDepartment.acronym || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
  await saveOrShare(pdf.output('blob'), fileName);
}
